import { useEffect, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import Button from "../shared/Button";
import "../../styles/StatisticsModal.css";

interface ScanByDate {
  date: string;
  count: number;
}

interface StatisticsData {
  shortCode: string;
  totalScans: number;
  firstScan: string | null;
  lastScan: string | null;
  scansByDate: ScanByDate[];
}

interface StatisticsModalProps {
  shortCode: string;
  name: string;
  isOpen: boolean;
  onClose: () => void;
}

type TimeRange = "7d" | "30d" | "90d" | "all";

const StatisticsModal: React.FC<StatisticsModalProps> = ({
  shortCode,
  name,
  isOpen,
  onClose,
}) => {
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");

  useEffect(() => {
    if (!isOpen) return;

    const fetchStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const backendUrl =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
        const userId = localStorage.getItem("userId");

        const response = await fetch(`${backendUrl}/stats/${shortCode}`, {
          headers: {
            "client-principal-user-id": userId || "",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch statistics");
        }

        const data = await response.json();
        setStatistics(data);
      } catch (err) {
        setError("Failed to load statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [isOpen, shortCode]);

  const getFilteredData = (): ScanByDate[] => {
    if (!statistics?.scansByDate) return [];

    const now = new Date();
    const ranges: Record<TimeRange, number> = {
      "7d": 7,
      "30d": 30,
      "90d": 90,
      all: Infinity,
    };

    const daysToShow = ranges[timeRange];
    if (daysToShow === Infinity) return statistics.scansByDate;

    const cutoffDate = new Date(now);
    cutoffDate.setDate(cutoffDate.getDate() - daysToShow);
    const cutoffStr = cutoffDate.toISOString().split("T")[0];

    return statistics.scansByDate.filter((item) => item.date >= cutoffStr);
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const calculateTrend = (): string => {
    const filteredData = getFilteredData();
    if (filteredData.length < 2) return "Not enough data";

    const midpoint = Math.floor(filteredData.length / 2);
    const firstHalf = filteredData.slice(0, midpoint);
    const secondHalf = filteredData.slice(midpoint);

    const firstAvg =
      firstHalf.reduce((sum, item) => sum + item.count, 0) / firstHalf.length;
    const secondAvg =
      secondHalf.reduce((sum, item) => sum + item.count, 0) / secondHalf.length;

    if (secondAvg > firstAvg * 1.1) return "Trending up";
    if (secondAvg < firstAvg * 0.9) return "Trending down";
    return "Stable";
  };

  const getTimeRangeLabel = (range: TimeRange): string => {
    if (range === "all") return "All Time";
    return `Last ${range.replace("d", " Days")}`;
  };

  if (!isOpen) return null;

  return (
    <div className="statistics-modal-overlay" onClick={onClose}>
      <div
        className="statistics-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="statistics-modal-header">
          <div className="statistics-modal-title-section">
            <h2>{name} Statistics Dashboard</h2>
          </div>
          <button
            className="statistics-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="statistics-modal-body">
          {loading ? (
            <div className="statistics-loading">Loading statistics...</div>
          ) : error ? (
            <div className="statistics-error">{error}</div>
          ) : statistics ? (
            <>
              <div className="statistics-cards">
                <div className="statistics-card">
                  <div className="statistics-card-label">Total Views</div>
                  <div className="statistics-card-value">
                    {statistics.totalScans.toLocaleString()}
                  </div>
                </div>
                <div className="statistics-card">
                  <div className="statistics-card-label">Trend</div>
                  <div className="statistics-card-value">
                    {calculateTrend()}
                  </div>
                </div>
                <div className="statistics-card">
                  <div className="statistics-card-label">First View</div>
                  <div className="statistics-card-value">
                    {statistics.firstScan
                      ? new Date(statistics.firstScan).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
                <div className="statistics-card">
                  <div className="statistics-card-label">Last View</div>
                  <div className="statistics-card-value">
                    {statistics.lastScan
                      ? new Date(statistics.lastScan).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className="statistics-time-range">
                {(["7d", "30d", "90d", "all"] as TimeRange[]).map((range) => (
                  <Button
                    key={range}
                    variant={timeRange === range ? "primary" : "secondary"}
                    onClick={() => setTimeRange(range)}
                    className="statistics-time-btn"
                  >
                    {getTimeRangeLabel(range)}
                  </Button>
                ))}
              </div>

              <div className="statistics-chart">
                {getFilteredData().length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={getFilteredData()}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorScans"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#5236AB"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor="#5236AB"
                            stopOpacity={0.05}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                      <XAxis
                        dataKey="date"
                        domain={["dataMin", "dataMax"]}
                        tickFormatter={formatDate}
                        stroke="#666666"
                        style={{ fontSize: "12px", fontWeight: "400" }}
                      />
                      <YAxis
                        stroke="#666666"
                        width={40}
                        style={{ fontSize: "12px", fontWeight: "400" }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #E0E0E0",
                          borderRadius: "4px",
                          padding: "8px 12px",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          fontSize: "14px",
                        }}
                        labelStyle={{
                          color: "#333333",
                          fontWeight: "600",
                          marginBottom: "4px",
                        }}
                        itemStyle={{
                          color: "#666666",
                        }}
                        labelFormatter={(label: string) =>
                          `Date: ${new Date(label).toLocaleDateString()}`
                        }
                        formatter={(value) => [value ?? 0, "Views"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#5236AB"
                        strokeWidth={2}
                        fill="url(#colorScans)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="statistics-no-data">
                    No scan data available for this period
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StatisticsModal;
